import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation, Navigate } from 'react-router-dom'
import AgentsPage from './pages/AgentsPage'
import AgentHistoryPage from './pages/AgentHistoryPage'
import AgentResultViewPage from './pages/AgentResultViewPage'
import SubscriptionPage from './pages/SubscriptionPage'
import PaymentConfirmationPage from './pages/PaymentConfirmationPage'
import AdminDashboard from './pages/AdminDashboard'
import UsagePage from './pages/UsagePage'


import Sidebar from './components/Sidebar'
import ErrorBoundary from './components/ErrorBoundary'
import AuthModal from './components/AuthModal'
import AuthPrompt from './components/AuthPrompt'
import { AgentLabel } from './components/AgentSelector'
import AgentToggle from './components/AgentToggle'
import AgentOptions from './components/AgentOptions'
import { AuthProvider, useAuth } from './hooks/useSupabaseAuth.jsx'
import { useSubscription } from './hooks/useSubscription'
import useAutoLogout from './hooks/useAutoLogout'
import { getAgentById, AVAILABLE_AGENTS } from './agents'
import { createLLMService } from './services/llmService'
import { Sparkles, Mic, ChevronDown, BookOpen, FileText, GraduationCap, Image, Video, Book, Newspaper, Search, TrendingUp, PenTool, Presentation, BarChart3, FlaskConical, FileType, Star, Users, Clock, Shield, Feather } from 'lucide-react'



// Agent Page Component
function AgentPage({ selectedAgent, setSelectedAgent }) {
  const { agentId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { hasActiveSubscription, consumeCredits, loading: subscriptionLoading } = useSubscription()
  
  const agent = getAgentById(agentId)
  
  // Get query from location state first, then from URL parameters (for continue editing)
  const urlParams = new URLSearchParams(location.search)
  const query = location.state?.query || urlParams.get('query') || ''
  const options = location.state?.options || {}
  
  // Debug logging for AgentPage
  console.log('🔍 AgentPage - agentId:', agentId)
  console.log('🔍 AgentPage - location.state:', location.state)
  console.log('🔍 AgentPage - options:', options)
  console.log('🔍 AgentPage - options.pdfFile:', options?.pdfFile)
  
  // Create LLM Service instance
  const llmService = useMemo(() => {
    const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY
    if (!apiKey) {
      console.error('❌ OpenRouter API key not found in environment variables')
      return null
    }
    try {
      return createLLMService(apiKey)
    } catch (error) {
      console.error('❌ Failed to create LLM Service:', error)
      return null
    }
  }, [])
  
  const handleBackToHome = () => {
    navigate('/')
  }
  
  if (!agent) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Agent Tidak Ditemukan</h2>
          <p className="text-text-tertiary mb-4">Agent yang Anda cari tidak tersedia.</p>
          <button
            onClick={handleBackToHome}
            className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary rounded-lg text-white transition-colors"
          >
            Kembali ke Home
          </button>
        </div>
      </div>
    )
  }

  const AgentComponent = agent.component

  // Show error if LLM Service failed to initialize
  if (!llmService) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Service Tidak Tersedia</h2>
          <p className="text-text-tertiary mb-4">LLM Service gagal diinisialisasi. Periksa konfigurasi API key.</p>
          <button
            onClick={handleBackToHome}
            className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary rounded-lg text-white transition-colors"
          >
            Kembali ke Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto">
      <AgentComponent 
        query={query}
        initialQuery={query}
        options={options}
        onBack={handleBackToHome}
        selectedAgent={selectedAgent}
        setSelectedAgent={setSelectedAgent}
        user={user}
        hasActiveSubscription={hasActiveSubscription}
        consumeCredits={consumeCredits}
        subscriptionLoading={subscriptionLoading}
        llmService={llmService}
      />
    </div>
  )
}

// Home Page Component - Clean Agent Selection
function HomePage({ selectedAgent, setSelectedAgent }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading } = useAuth()
  const { hasActiveSubscription, creditsRemaining, packageName } = useSubscription()
  
  // State for input
  const [query, setQuery] = useState('')

  const [showLoginModal, setShowLoginModal] = useState(false)
  const [agentOptions, setAgentOptions] = useState({})

  // Get available agents for selection grid
  const agents = AVAILABLE_AGENTS

  


  // Handle selectedAgent from navigation state (from AgentsPage)
  useEffect(() => {
    if (location.state?.selectedAgentId && !selectedAgent) {
      const agent = getAgentById(location.state.selectedAgentId)
      if (agent) {
        setSelectedAgent(agent)
      }
      // Clear the state to prevent re-setting on refresh
      navigate('/', { replace: true, state: {} })
    }
  }, [location.state, selectedAgent, setSelectedAgent, navigate])
  


  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent)
    
    // Set default options for specific agents
    const defaultOptions = {}
    if (agent.id === 'gambar_agent') {
      defaultOptions.aspectRatio = '1:1'
    } else if (agent.id === 'berita_agent') {
      defaultOptions.language = 'id'
    } else if (agent.id === 'trending_agent') {
      const today = new Date().toISOString().split('T')[0]
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const yesterdayStr = yesterday.toISOString().split('T')[0]
      defaultOptions.fromDate = yesterdayStr
      defaultOptions.toDate = today
      defaultOptions.region = 'Indonesia'
    } else if (agent.id === 'chart_agent') {
      defaultOptions.inputType = 'topic'
    } else if (agent.id === 'video_agent') {
      defaultOptions.videoType = 'text_to_video'
      defaultOptions.aspectRatio = '16:9'
    }
    setAgentOptions(defaultOptions)
  }
  


  const handleAgentClear = () => {
    setSelectedAgent(null)
    setAgentOptions({})
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!user) {
      setShowLoginModal(true)
      return
    }
    
    if (!selectedAgent) {
      alert('Pilih agent terlebih dahulu')
      return
    }
    
    if (!query.trim()) {
      alert('Masukkan query terlebih dahulu')
      return
    }

    // Special validation for PDF Agent
    if (selectedAgent.id === 'pdf_agent' && !agentOptions.pdfFile) {
      alert('Silakan upload file PDF terlebih dahulu')
      return
    }

    // Navigate to agent with query
    const options = {
      ...agentOptions
    }
    
    // Debug logging for navigation
    console.log('🔍 HomePage - Navigating to agent:', selectedAgent.id)
    console.log('🔍 HomePage - agentOptions:', agentOptions)
    console.log('🔍 HomePage - final options:', options)
    console.log('🔍 HomePage - pdfFile in options:', options.pdfFile)
    
    // Navigate to regular agent
    navigate(`/agent/${selectedAgent.id}`, { 
      state: { 
        query: query.trim(),
        options 
      } 
    })
  }



  // Show loading state during auth transitions
  if (loading) {
    return (
      <div className="flex-1 flex flex-col bg-bg-primary overflow-x-hidden">
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-text-tertiary">Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-bg-primary overflow-x-hidden">
      {/* Main content */}
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <img 
                src="https://media.cara.id/images/logo-cara-white.png" 
                alt="Cara Logo" 
                className="h-16"
              />
            </div>
          </div>

          {/* Welcome Message */}
          {user && !selectedAgent && (
            <div className="text-center mb-8 transition-all duration-500 ease-in-out">
              <p className="text-white font-light text-sm sm:text-lg" style={{ fontWeight: '300' }}>
                Tenang {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}, semua pasti ada caranya...
              </p>
            </div>
          )}

          {/* Input Form */}
          <div className="w-full max-w-4xl mx-auto transition-all duration-500 ease-in-out">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Input Container */}
              <div className="relative group transition-all duration-300 ease-in-out">
                <div className="relative bg-white/8 backdrop-blur-xl border border-white/20 rounded-2xl transition-all duration-300 group-focus-within:border-white/50">
                
                <div className="relative flex flex-col p-6 space-y-4">


                  {/* Input container */}
                  <div className="relative">
                    <textarea
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={selectedAgent 
                        ? (selectedAgent.name === 'Skripsi Agent' ? 'Masukkan judul skripsi yang ingin Anda buat...' 
                          : selectedAgent.name === 'Artikel Agent' ? 'Masukkan judul artikel yang menarik...' 
                          : selectedAgent.name === 'Gambar Agent' ? 'Deskripsikan gambar yang ingin Anda buat...' 
                          : selectedAgent.name === 'Makalah Agent' ? 'Masukkan topik makalah akademis...' 
                          : selectedAgent.name === 'Berita Agent' ? 'Masukkan topik berita...' 
                          : selectedAgent.name === 'Buku Agent' ? 'Masukkan tema buku yang ingin ditulis...'
                          : selectedAgent.name === 'Novel Agent' ? 'Masukkan judul novel yang ingin dibuat...'
                          : selectedAgent.name === 'Trending Agent' ? 'Masukkan topik yang Anda cari...'
                          : selectedAgent.name === 'Search Agent' ? 'Masukkan topik yang ingin Anda cari...'
                          : selectedAgent.name === 'Writing Agent' ? 'Masukkan topik yang ingin Anda tulis...'
                          : selectedAgent.name === 'Video Agent' ? 'Deskripsikan video yang ingin Anda buat...'
                          : selectedAgent.name === 'PPT Agent' ? 'Masukkan topik PPT...'
                          : selectedAgent.name === 'Research Agent' ? 'Masukkan topik penelitian yang ingin Anda buat...'
                          : selectedAgent.name === 'PDF Agent' ? 'Masukkan perintah untuk PDF...'
                          : selectedAgent.name === 'Chart Agent' ? (agentOptions.inputType === 'data' ? 'Masukkan data Anda (contoh: Penjualan Q1: 100jt, Q2: 150jt...)' : 'Masukkan topik chart (contoh: Pertumbuhan ekonomi Asia 2024)')
                          : `Masukkan input untuk ${selectedAgent.name}...`) 
                        : "Pilih agent, lalu masukkan query Anda..."
                      }
                      className="w-full bg-transparent text-white/90 placeholder-gray-400 border-none outline-none resize-none text-lg leading-relaxed transition-all duration-200 ease-in-out focus:placeholder-gray-500 focus:text-white selection:bg-white/25"
                      rows={1}
                      style={{
                        height: 'auto',
                        minHeight: '60px',
                        maxHeight: '200px',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                      }}
                      onInput={(e) => {
                        e.target.style.height = 'auto'
                        e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px'
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          if (query.trim() && selectedAgent && user) {
                            handleSubmit(e)
                          }
                        }
                      }}
                    />
                    
                    {/* Bottom row with actions */}
                    <div className="flex items-center justify-between mt-6 pt-5 border-t border-white/10">
                      {/* Left side - Agent Toggle and Character count */}
                      <div className="flex items-center space-x-4">
                        <AgentToggle 
                          selectedAgent={selectedAgent}
                          onAgentSelect={handleAgentSelect}
                          onAgentClear={handleAgentClear}
                        />
                        
                        {query.length > 0 && (
                          <div className="text-xs text-gray-400 font-mono">
                            <span className="text-white/70">{query.length}</span>
                            <span className="text-gray-500">/2000</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Right side - Submit button */}
                      <button
                        type="submit"
                        disabled={!query.trim() || !selectedAgent || !user}
                        className={`px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200 flex items-center space-x-2 ${
                          !query.trim() || !selectedAgent || !user
                            ? "bg-white/5 text-gray-500 cursor-not-allowed"
                            : "bg-white text-black hover:bg-white/90 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                        }`}
                      >
                        <Sparkles className={`w-4 h-4 transition-all duration-200 ${
                          !query.trim() || !selectedAgent || !user ? "" : "group-hover:rotate-12"
                        }`} />
                        <span>Kirim</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Description and Options */}
            {selectedAgent && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 space-y-4 transition-all duration-500 ease-in-out transform animate-in slide-in-from-top-2 fade-in-0">
                {/* Agent Description */}
                <div>
                  <h3 className="text-sm font-medium text-white mb-2">{selectedAgent.name}</h3>
                  <p className="text-sm text-gray-400">{selectedAgent.description}</p>
                </div>
                
                {/* Agent-specific Options */}
                {selectedAgent.id === 'gambar_agent' && (
                  <AgentOptions 
                    agentType="gambar_agent"
                    options={agentOptions}
                    onOptionsChange={setAgentOptions}
                  />
                )}
                
                {selectedAgent.id === 'berita_agent' && (
                  <AgentOptions 
                    agentType="berita_agent"
                    options={agentOptions}
                    onOptionsChange={setAgentOptions}
                  />
                )}
                
                {selectedAgent.id === 'trending_agent' && (
                  <AgentOptions 
                    agentType="trending_agent"
                    options={agentOptions}
                    onOptionsChange={setAgentOptions}
                  />
                )}
                
                {selectedAgent.id === 'chart_agent' && (
                  <AgentOptions 
                    agentType="chart_agent"
                    options={agentOptions}
                    onOptionsChange={setAgentOptions}
                  />
                )}
                
                {selectedAgent.id === 'research_agent' && (
                  <AgentOptions 
                    agentType="research_agent"
                    options={agentOptions}
                    onOptionsChange={setAgentOptions}
                  />
                )}
                
                {selectedAgent.id === 'video_agent' && (
                  <AgentOptions 
                    agentType="video_agent"
                    options={agentOptions}
                    onOptionsChange={setAgentOptions}
                  />
                )}
                
                {selectedAgent.id === 'pdf_agent' && (
                  <div>
                    {console.log('🔍 HomePage - Rendering PDF AgentOptions')}
                    <AgentOptions 
                      agentType="pdf_agent"
                      options={agentOptions}
                      onOptionsChange={setAgentOptions}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Auth/Credit Status */}
            {!user ? (
              <div className="text-center text-gray-400 text-sm">
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Login
                </button>
                {" "}untuk menggunakan AI Agents
              </div>
            ) : hasActiveSubscription ? (
              <div className="text-center text-green-400 text-sm">
                ✓ {packageName || 'Member'} - {creditsRemaining !== null && creditsRemaining !== undefined ? creditsRemaining.toLocaleString('id-ID') : '0'} kredit
              </div>
            ) : (
              <div className="text-center text-yellow-400 text-sm">
                <button
                  onClick={() => navigate('/subscription')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Upgrade to Pro
                </button>
                {" "}untuk akses penuh
              </div>
            )}
            </form>
          </div>

          {/* Arrow Down - Only for non-logged users */}
          {!user && (
            <div className="text-center mt-12">
              <button 
                onClick={() => {
                  const landingSection = document.getElementById('landing-section')
                  if (landingSection) {
                    landingSection.scrollIntoView({ behavior: 'smooth' })
                  }
                }}
                className="animate-bounce hover:animate-none transition-all duration-300 hover:text-white/80 focus:outline-none"
              >
                <ChevronDown className="w-8 h-8 text-white/60 mx-auto" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Landing Page - Only for non-logged users */}
      {!user && (
        <div id="landing-section" className="min-h-screen px-4 py-16">
          <div className="max-w-6xl mx-auto">
            {/* Main Title */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Semua Pasti Ada Caranya...
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 font-light max-w-3xl mx-auto">
                Platform AI terlengkap untuk semua kebutuhan kreatif dan akademis Anda
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {/* Search Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-cyan-500/20 rounded-lg mr-4 group-hover:bg-cyan-500/30 transition-colors">
                    <Search className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Search Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Menjawab pertanyaan dengan informasi berkualitas dari sumber web dan akademik yang terpercaya
                </p>
              </div>

              {/* Writing Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-violet-600/20 rounded-lg mr-4 group-hover:bg-violet-600/30 transition-colors">
                    <PenTool className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Writing Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Editor dokumen AI dengan fitur editing, parafrase, dan berbagai tools penulisan cerdas
                </p>
              </div>

              {/* Skripsi Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-emerald-500/20 rounded-lg mr-4 group-hover:bg-emerald-500/30 transition-colors">
                    <BookOpen className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Skripsi Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat draft skripsi lengkap dengan 25-40 ribu kata sesuai kaidah penulisan Indonesia
                </p>
              </div>

              {/* Artikel Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg mr-4 group-hover:bg-blue-500/30 transition-colors">
                    <FileText className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Artikel Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat artikel web lengkap dengan 2500-4000 kata yang engaging dan informatif
                </p>
              </div>

              {/* Makalah Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-violet-500/20 rounded-lg mr-4 group-hover:bg-violet-500/30 transition-colors">
                    <GraduationCap className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Makalah Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat makalah akademik lengkap dengan 10.000-15.000 kata sesuai standar penelitian
                </p>
              </div>

              {/* Gambar Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-pink-500/20 rounded-lg mr-4 group-hover:bg-pink-500/30 transition-colors">
                    <Image className="w-6 h-6 text-pink-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Gambar Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat gambar AI berkualitas tinggi dengan berbagai pilihan aspect ratio
                </p>
              </div>

              {/* Video Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-orange-500/20 rounded-lg mr-4 group-hover:bg-orange-500/30 transition-colors">
                    <Video className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Video Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat video AI dari teks atau gambar dengan kualitas tinggi dan berbagai opsi durasi
                </p>
              </div>

              {/* Buku Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-amber-500/20 rounded-lg mr-4 group-hover:bg-amber-500/30 transition-colors">
                    <Book className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Buku Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat buku non-fiksi edukatif dengan 250-500 halaman, struktur sistematis, dan referensi valid
                </p>
              </div>

              {/* Berita Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-red-500/20 rounded-lg mr-4 group-hover:bg-red-500/30 transition-colors">
                    <Newspaper className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Berita Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat artikel berita aktual dan original dengan referensi dari 10-20 sumber portal berita terpercaya
                </p>
              </div>

              {/* Trending Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-orange-500/20 rounded-lg mr-4 group-hover:bg-orange-500/30 transition-colors">
                    <TrendingUp className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Trending Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Deteksi trend terbaru di internet dengan filter tanggal dan region
                </p>
              </div>

              {/* PPT Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-purple-600/20 rounded-lg mr-4 group-hover:bg-purple-600/30 transition-colors">
                    <Presentation className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">PPT Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat rancangan presentasi PowerPoint lengkap dengan slide menarik, grafik, dan elemen visual
                </p>
              </div>

              {/* Chart Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-emerald-600/20 rounded-lg mr-4 group-hover:bg-emerald-600/30 transition-colors">
                    <BarChart3 className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Chart Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat 5 jenis chart visual (Pie, Bar, Line, Doughnut, Area) dari data atau topik yang dapat didownload sebagai gambar
                </p>
              </div>

              {/* Research Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-red-600/20 rounded-lg mr-4 group-hover:bg-red-600/30 transition-colors">
                    <FlaskConical className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Research Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat research paper komprehensif yang diterima secara akademik dengan visualisasi data dan dapat diexport ke PDF/PPT
                </p>
              </div>

              {/* PDF Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-indigo-600/20 rounded-lg mr-4 group-hover:bg-indigo-600/30 transition-colors">
                    <FileType className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">PDF Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Analisis dan ekstraksi informasi dari dokumen PDF dengan kemampuan menjawab pertanyaan berdasarkan konten dokumen
                </p>
              </div>

              {/* Novel Agent */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-indigo-500/20 rounded-lg mr-4 group-hover:bg-indigo-500/30 transition-colors">
                    <Feather className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Novel Agent</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Membuat novel lengkap dengan berbagai struktur plot cerita yang menarik dan karakter yang mendalam
                </p>
              </div>
            </div>

            {/* Why Choose Us Section */}
            <div className="mb-16 mt-24 md:mt-32">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Mengapa Memilih Cara AI?
                </h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                  Platform AI terdepan dengan teknologi canggih untuk semua kebutuhan kreatif dan akademis Anda
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/8 transition-all duration-300 group">
                  <div className="p-4 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Star className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Kualitas Premium</h3>
                  <p className="text-gray-300 leading-relaxed">Output berkualitas tinggi dengan standar akademik dan profesional</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/8 transition-all duration-300 group">
                  <div className="p-4 bg-gradient-to-br from-green-500/30 to-green-600/30 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Cepat & Efisien</h3>
                  <p className="text-gray-300 leading-relaxed">Hemat waktu dengan hasil yang dapat diselesaikan dalam hitungan menit</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/8 transition-all duration-300 group">
                  <div className="p-4 bg-gradient-to-br from-purple-500/30 to-purple-600/30 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Mudah Digunakan</h3>
                  <p className="text-gray-300 leading-relaxed">Interface yang intuitif, cocok untuk pemula hingga profesional</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/8 transition-all duration-300 group">
                  <div className="p-4 bg-gradient-to-br from-orange-500/30 to-orange-600/30 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Aman & Terpercaya</h3>
                  <p className="text-gray-300 leading-relaxed">Data Anda aman dengan enkripsi tingkat enterprise</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-3xl backdrop-blur-sm"></div>
              <div className="relative bg-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-12 text-center">
                <div className="mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full mb-6">
                    <Sparkles className="w-10 h-10 text-blue-400" />
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                    Siap Memulai Perjalanan AI Anda?
                  </h2>
                  <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                    Bergabunglah dengan ribuan pengguna yang telah merasakan kemudahan berkarya dengan AI
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="group relative px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-lg rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-blue-500/30"
                  >
                    <span className="relative z-10 flex items-center space-x-3">
                      <span>Mulai Gratis Sekarang</span>
                      <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </button>
                </div>
                
                <div className="mt-6 text-sm text-gray-400">
                  ✨ Gratis untuk memulai • Akses instan
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      


      {/* Auth Modal */}
      {showLoginModal && (
        <AuthModal 
          isOpen={showLoginModal} 
          onClose={() => setShowLoginModal(false)} 
        />
      )}
    </div>
  )
}

// Main App Component
function App() {
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const { user, loading } = useAuth()
  const location = useLocation()

  // Auto-logout hook
  useAutoLogout(60) // 60 minutes

  // Reset selected agent when user logs out
  useEffect(() => {
    if (!user && !loading) {
      setSelectedAgent(null)
    }
  }, [user, loading])

  // Listen for auth modal open events
  useEffect(() => {
    const handleOpenAuthModal = () => {
      setShowAuthModal(true)
    }

    window.addEventListener('openAuthModal', handleOpenAuthModal)
    return () => {
      window.removeEventListener('openAuthModal', handleOpenAuthModal)
    }
  }, [])

  // Show loading state during auth transitions
  if (loading) {
    return (
      <div className="min-h-screen flex bg-bg-primary text-text-primary">
        <Sidebar onAuthModalOpen={() => setShowAuthModal(true)} />
        <div className="flex-1 flex flex-col md:ml-16 pb-16 md:pb-0">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-text-tertiary">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex bg-bg-primary text-text-primary">
      <Sidebar onAuthModalOpen={() => setShowAuthModal(true)} />
      
      <div className="flex-1 flex flex-col md:ml-16 pb-16 md:pb-0">
        <Routes>
          <Route 
            path="/" 
            element={
              <HomePage 
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
              />
            } 
          />
          <Route 
            path="/agents" 
            element={
              <AgentsPage 
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
              />
            } 
          />
          <Route 
            path="/agent/:agentId" 
            element={
              <AgentPage 
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
              />
            } 
          />

          <Route 
            path="/agent-history" 
            element={<AgentHistoryPage />} 
          />
          <Route 
            path="/usage" 
            element={<UsagePage />} 
          />
          <Route 
            path="/agent-result/:historyId" 
            element={<AgentResultViewPage />} 
          />
          <Route 
            path="/agents/:agentType/result/:id" 
            element={<AgentResultViewPage />} 
          />
          <Route 
            path="/subscription" 
            element={<SubscriptionPage />} 
          />
          <Route 
            path="/payment-confirmation/:orderId" 
            element={<PaymentConfirmationPage />} 
          />
          <Route 
            path="/admin" 
            element={<AdminDashboard />} 
          />
          {/* Catch-all route for invalid URLs */}
          <Route 
            path="*" 
            element={<Navigate to="/" replace />} 
          />
        </Routes>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      )}


    </div>
  )
}

// Wrapped App with Providers
function AppWrapper() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  )
}

export default AppWrapper
